import React, { useState, useEffect } from 'react';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { Clock, Users, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { format } from 'date-fns';
import ChatBox from '../components/ChatBox';

const ChefDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const socket = useSocket();
  const { logout } = useAuth();

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  useEffect(() => {
    if (socket) {
      socket.on('new_order', (order) => {
        toast.success(`New order from Table ${order.table_number}!`);
        fetchOrders();
      });
      socket.on('order_updated', () => {
        fetchOrders();
      });
      return () => {
        socket.off('new_order');
        socket.off('order_updated');
      };
    }
  }, [socket]);

  const fetchOrders = async () => {
    try {
      const response = await api.getOrders(filter === 'all' ? null : filter);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.updateOrderStatus(orderId, newStatus);
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Cancel this order? This will be recorded.')) return;
    try {
      await api.updateOrderStatus(orderId, 'cancelled', 'chef');
      toast.success('Order cancelled and recorded');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-red-100 text-red-700 border-red-200',
      preparing: 'bg-orange-100 text-orange-700 border-orange-200',
      ready: 'bg-green-100 text-green-700 border-green-200',
      delivered: 'bg-gray-100 text-gray-600 border-gray-200',
      cancelled: 'bg-red-50 text-red-500 border-red-200',
    };
    return colors[status] || colors.pending;
  };

  const tabs = ['pending', 'preparing', 'ready', 'all'];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-900">Kitchen Display</h1>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {format(new Date(), 'MMM dd, yyyy • HH:mm')}
              </div>
              <Button variant="outline" onClick={logout} className="h-9" data-testid="chef-logout-button">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Orders Section */}
          <div className="lg:col-span-2 space-y-4">
            {/* Filter Tabs */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex gap-2 overflow-x-auto">
                {tabs.map(status => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap font-medium transition ${
                      filter === status
                        ? status === 'cancelled' ? 'bg-red-500 text-white' : 'bg-[#E65100] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`filter-${status}-button`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Orders Grid */}
            {loading ? (
              <div className="text-center py-12 text-gray-600">Loading orders...</div>
            ) : orders.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <p className="text-gray-500">No orders found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {orders.map(order => (
                  <div
                    key={order.id}
                    className={`bg-white rounded-xl border-2 p-5 hover:shadow-lg transition ${
                      order.status === 'pending' ? 'border-red-300' :
                      order.status === 'cancelled' ? 'border-red-200 opacity-80' :
                      'border-gray-200'
                    }`}
                    data-testid={`order-card-${order.id}`}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Users className="w-5 h-5 text-[#E65100]" />
                          <span className="text-2xl font-bold text-gray-900">Table {order.table_number}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          {format(new Date(order.created_at), 'HH:mm')}
                        </div>
                      </div>
                      <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${getStatusColor(order.status)}`}>
                        {order.status.toUpperCase()}
                      </span>
                    </div>

                    {/* Customer Comment */}
                    {order.customer_comment && (
                      <div className="mb-3 p-2.5 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                        <span className="font-semibold">Customer note: </span>{order.customer_comment}
                      </div>
                    )}

                    {/* Cancelled by */}
                    {order.status === 'cancelled' && order.cancelled_by && (
                      <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5" />
                        Cancelled by {order.cancelled_by}
                      </div>
                    )}

                    {/* Items */}
                    <div className="space-y-2 mb-4">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-2.5">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-900">{item.name}</span>
                            <span className="text-sm font-bold text-[#E65100]">x{item.quantity}</span>
                          </div>
                          {/* Show modifier choices so chef knows exactly what customer wants */}
                          {item.modifiers && item.modifiers.length > 0 && (
                            <div className="mt-1.5 space-y-0.5 pl-1 border-l-2 border-orange-300">
                              {item.modifiers.map((mod, mi) => (
                                <p key={mi} className="text-xs text-gray-500">
                                  <span className="font-medium text-gray-600">{mod.group_title}:</span>{' '}
                                  {mod.chosen.join(', ')}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Total */}
                    <div className="border-t border-gray-200 pt-3 mb-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">Subtotal</span>
                        <span className="text-lg font-bold text-gray-900">₹{order.subtotal.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {order.status !== 'cancelled' && order.status !== 'delivered' && (
                      <div className="grid grid-cols-2 gap-2">
                        {order.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => updateOrderStatus(order.id, 'preparing')}
                              className="bg-orange-600 hover:bg-orange-700 text-white"
                              data-testid={`start-preparing-${order.id}-button`}
                            >
                              Start Preparing
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => updateOrderStatus(order.id, 'ready')}
                              data-testid={`mark-ready-${order.id}-button`}
                            >
                              Mark Ready
                            </Button>
                          </>
                        )}
                        {order.status === 'preparing' && (
                          <Button
                            onClick={() => updateOrderStatus(order.id, 'ready')}
                            className="col-span-2 bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`mark-ready-${order.id}-button`}
                          >
                            Mark as Ready
                          </Button>
                        )}
                        {order.status === 'ready' && (
                          <Button
                            onClick={() => updateOrderStatus(order.id, 'delivered')}
                            className="col-span-2 bg-gray-600 hover:bg-gray-700 text-white"
                            data-testid={`mark-delivered-${order.id}-button`}
                          >
                            Mark as Delivered
                          </Button>
                        )}
                        {/* Cancel button — available for pending and preparing */}
                        {(order.status === 'pending' || order.status === 'preparing') && (
                          <button
                            onClick={() => cancelOrder(order.id)}
                            className="col-span-2 mt-1 py-2 text-sm font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition"
                          >
                            Cancel Order
                          </button>
                        )}
                      </div>
                    )}

                    {order.status === 'delivered' && (
                      <div className="text-center text-sm text-gray-500 py-2">Order completed ✓</div>
                    )}
                    {order.status === 'cancelled' && (
                      <div className="text-center text-sm text-red-400 py-2 flex items-center justify-center gap-1">
                        <XCircle className="w-4 h-4" /> Order cancelled
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Chat Section */}
          <div className="lg:col-span-1">
            <ChatBox role="chef" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChefDashboard;

